import { useState, useEffect } from "react";
import "@style/Admin.scss";
import "@style/sass/_accounting.scss";
import BarChart3D from "@/elements/charts/BarChart3D";
import StackedBarChart from "@/elements/charts/StackedBarChart";
import RightTimeApi from "@/API";
import { appendBillingOrgQuery } from "@/utils/billingOrgHelpers";

export default function AccountingDashboardTab(props) {
    const { isActive, updateSpinner, refreshKey, onDataChanged, billingOrgId = 0 } = props;
    
    // State for dashboard data
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Derived data for charts - will be calculated from API response
    const [revenueData, setRevenueData] = useState([]);
    const [billingData, setBillingData] = useState([]);
    const [receivablesData, setReceivablesData] = useState([]);
    const [testTypesData, setTestTypesData] = useState<any>({});
    const [billingMisconfigurations, setBillingMisconfigurations] = useState([]);

    // Dashboard section visibility controls
    const [visibleSections, setVisibleSections] = useState({
        financial: true,
        testTypes: true,
        stats: true,
        billingConfig: true
    });

    // Active view tracking
    const [activeView, setActiveView] = useState('all');

    // Fetch dashboard data from API
    async function fetchDashboardData() {
        setLoading(true);
        setError(null);
        
        try {
            updateSpinner(true);
            const response = await RightTimeApi.getAsync(appendBillingOrgQuery("/Dashboard", billingOrgId));
            updateSpinner(false);
            
            if (response && response.ok && response.jsonData) {
                setDashboardData(response.jsonData);
                processChartData(response.jsonData);
            } else {
                throw new Error(response?.message || "Failed to fetch dashboard data");
            }
        } catch (err) {
            console.error("Error fetching dashboard data:", err);
            setError(err.message || "Failed to load dashboard data");
            updateSpinner(false);
        } finally {
            setLoading(false);
        }
    }

    // Process API response data into chart format
    function processChartData(data) {
        if (!data.labels || !Array.isArray(data.labels)) {
            console.warn("Invalid API response: missing labels array");
            setFallbackData();
            return;
        }

        // Format labels to be more readable (e.g., "2024-12" -> "Dec 2024")
        const formatLabel = (label) => {
            const [year, month] = label.split('-');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${monthNames[parseInt(month) - 1]} ${year}`;
        };

        // Process monthly revenue data
        if (data.monthlyRevenue && Array.isArray(data.monthlyRevenue)) {
            const processedRevenue = data.labels.map((label, index) => ({
                label: formatLabel(label),
                value: parseFloat(data.monthlyRevenue[index] || 0)
            }));
            setRevenueData(processedRevenue);
        }

        // Process monthly billing data
        if (data.monthlyBilling && Array.isArray(data.monthlyBilling)) {
            const processedBilling = data.labels.map((label, index) => ({
                label: formatLabel(label),
                value: parseFloat(data.monthlyBilling[index] || 0)
            }));
            setBillingData(processedBilling);
        }

        // Process monthly receivables data
        if (data.monthlyReceivables && Array.isArray(data.monthlyReceivables)) {
            const processedReceivables = data.labels.map((label, index) => ({
                label: formatLabel(label),
                value: parseFloat(data.monthlyReceivables[index] || 0)
            }));
            setReceivablesData(processedReceivables);
        }

        // Process monthly test types data
        if (data.monthlyTestsByType && typeof data.monthlyTestsByType === 'object') {
            setTestTypesData(data.monthlyTestsByType);
        }

        if (Array.isArray(data.billingMisconfigurations)) {
            setBillingMisconfigurations(data.billingMisconfigurations);
        } else {
            setBillingMisconfigurations([]);
        }
    }

    // Fallback sample data if API fails
    function setFallbackData() {
        setRevenueData([
            { label: 'Jan 2025', value: 12500 },
            { label: 'Feb 2025', value: 15300 },
            { label: 'Mar 2025', value: 18700 },
            { label: 'Apr 2025', value: 14200 },
            { label: 'May 2025', value: 16800 },
            { label: 'Jun 2025', value: 19200 }
        ]);

        setBillingData([
            { label: 'Jan 2025', value: 32000 },
            { label: 'Feb 2025', value: 28500 },
            { label: 'Mar 2025', value: 35200 },
            { label: 'Apr 2025', value: 29800 },
            { label: 'May 2025', value: 31500 },
            { label: 'Jun 2025', value: 33700 }
        ]);

        setReceivablesData([
            { label: 'Jan 2025', value: 8500 },
            { label: 'Feb 2025', value: 12300 },
            { label: 'Mar 2025', value: 4200 },
            { label: 'Apr 2025', value: 18000 },
            { label: 'May 2025', value: 9000 },
            { label: 'Jun 2025', value: 15400 }
        ]);

        // Sample test types data
        setTestTypesData({
            "A-PAS": [18, 21, 7, 15, 37, 73],
            "C-PAS": [4, 12, 10, 12, 4, 10],
            "C-PAS - Current": [7, 35, 0, 54, 6, 0],
            "C-PAS - Ideal": [14, 35, 13, 19, 38, 107],
            "Behavioral Expectations": [0, 0, 0, 0, 0, 10]
        });
    }

    // Fetch data when tab becomes active or refresh is triggered
    useEffect(() => {
        if (isActive) {
            fetchDashboardData();
        }
    }, [isActive, refreshKey, billingOrgId]);

    if (!isActive) return null;

    // Create formatted labels for test types chart (same as other charts)
    const getFormattedLabels = () => {
        if (dashboardData?.labels) {
            return dashboardData.labels.map(label => {
                const [year, month] = label.split('-');
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return `${monthNames[parseInt(month) - 1]} ${year}`;
            });
        }
        // Fallback labels for sample data
        return ['Jan 2025', 'Feb 2025', 'Mar 2025', 'Apr 2025', 'May 2025', 'Jun 2025'];
    };

    // Toggle section visibility
    const toggleSection = (sectionKey) => {
        setVisibleSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Quick preset functions
    const showAll = () => {
        setVisibleSections({
            financial: true,
            testTypes: true,
            stats: true,
            billingConfig: true
        });
        setActiveView('all');
    };

    const showFinancialOnly = () => {
        setVisibleSections({
            financial: true,
            testTypes: false,
            stats: true,
            billingConfig: true
        });
        setActiveView('financial');
    };

    const showTestsOnly = () => {
        setVisibleSections({
            financial: false,
            testTypes: true,
            stats: false,
            billingConfig: false
        });
        setActiveView('tests');
    };

    // Show loading state
    if (loading && (!revenueData.length && !billingData.length && !receivablesData.length && !Object.keys(testTypesData).length)) {
        return (
            <div className="accountingDashboard">
                <div className="dashboardHeader">
                    <h2>Accounting Dashboard</h2>
                    <p>Loading dashboard data...</p>
                </div>
                <div className="dashboardLoadingCenter">
                    <div className="spinner"></div>
                </div>
            </div>
        );
    }

    // Show error state
    if (error && (!revenueData.length && !billingData.length && !receivablesData.length && !Object.keys(testTypesData).length)) {
        return (
            <div className="accountingDashboard">
                <div className="dashboardHeader">
                    <h2>Accounting Dashboard</h2>
                    <p className="dashboardErrorText">Error loading data: {error}</p>
                </div>
                <div className="dashboardLoadingCenter">
                    <button 
                        onClick={fetchDashboardData}
                        className="dashboardRetryButton"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="accountingDashboard">
            <div className="dashboardHeader">
                <h2>Accounting Dashboard</h2>
                {error && (
                    <div className="dashboardWarningNote">
                        Note: Using sample data due to API error
                    </div>
                )}
            </div>

            {/* Dashboard Controls */}
            <div className="dashboardControls">
                <div className="controlSection">
                    <div className="quickViewButtons">
                        <button 
                            className={`btn ${activeView === 'financial' ? 'btn-active' : 'btn-outline'}`}
                            onClick={showFinancialOnly}
                            title="Show financial charts and stats only"
                        >
                            Financial Only
                        </button>
                        <button 
                            className={`btn ${activeView === 'tests' ? 'btn-active' : 'btn-outline'}`}
                            onClick={showTestsOnly}
                            title="Show test data only"
                        >
                            Tests Only
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Financial Charts Section */}
            {visibleSections.financial && (
                <div className="dashboardSection">
                    <h3 className="sectionTitle">Financial Overview</h3>
                    <div className="dashboardGrid">
                        <div className="chartContainer">
                            <BarChart3D 
                                data={revenueData}
                                title="Monthly Revenue ($)"
                                maxHeight={3}
                            />
                        </div>
                        
                        <div className="chartContainer">
                            <BarChart3D 
                                data={billingData}
                                title="Monthly Billing ($)"
                                maxHeight={2.5}
                            />
                        </div>
                        
                        <div className="chartContainer">
                            <BarChart3D 
                                data={receivablesData}
                                title="Monthly Receivables ($)"
                                maxHeight={2.5}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Test Types Section */}
            {visibleSections.testTypes && Object.keys(testTypesData).length > 0 && (
                <div className="dashboardSection">
                    <h3 className="sectionTitle">Test Analytics</h3>
                    <div className="dashboardGrid">
                        <div className="chartContainer stackedChart">
                            <StackedBarChart 
                                data={testTypesData}
                                labels={getFormattedLabels()}
                                title="Monthly Tests by Type"
                            />
                        </div>
                    </div>
                </div>
            )}

            {visibleSections.billingConfig && (
                <div className="dashboardSection billingMisconfigSection">
                    <h3 className="sectionTitle">Billing Configuration</h3>
                    {billingMisconfigurations.length > 0 ? (
                        <>
                            <p className="billingMisconfigWarning">
                                {billingMisconfigurations.length} billable test type(s) need fee schedule configuration before tests can be billed.
                            </p>
                            <table className="billingMisconfigTable">
                                <thead>
                                    <tr>
                                        <th>Company</th>
                                        <th>Test Type</th>
                                        <th>Issue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billingMisconfigurations.map((row) => (
                                        <tr key={`${row.companyId}-${row.companyTestTypeId}`}>
                                            <td>{row.companyName}</td>
                                            <td>{row.companyTestTypeName}</td>
                                            <td>{row.issue}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <p className="billingMisconfigEmpty">All billable test types have active fee schedule items configured.</p>
                    )}
                </div>
            )}
            
            {/* Summary Stats Section */}
            {visibleSections.stats && (
                <div className="dashboardSection">
                    <h3 className="sectionTitle">Summary Statistics</h3>
                    <div className="dashboardStats">
                        <div className="statCard">
                            <h4>Total Revenue</h4>
                            <span className="statValue">
                                ${revenueData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="statCard">
                            <h4>Total Billing</h4>
                            <span className="statValue">
                                ${billingData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="statCard">
                            <h4>Total Receivables</h4>
                            <span className="statValue">
                                ${receivablesData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="statCard">
                            <h4>Total Tests</h4>
                            <span className="statValue">
                                {Object.keys(testTypesData).length > 0 ? 
                                    Object.values(testTypesData)
                                        .reduce((total, testArray) => 
                                            total + (Array.isArray(testArray) ? testArray.reduce((sum, count) => sum + Number(count || 0), 0) : 0), 0)
                                        .toLocaleString()
                                    : '0'
                                }
                            </span>
                        </div>
                        <div className="statCard">
                            <h4>Outstanding Balance</h4>
                            <span className="statValue">
                                ${(billingData.reduce((sum, item) => sum + item.value, 0) - 
                                   revenueData.reduce((sum, item) => sum + item.value, 0)).toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
